import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, Upload, LogOut, Search, 
  Loader2, AlertCircle, Check, X, Image as ImageIcon,
  LayoutGrid, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import adminApi from '../services/adminApi';
import galleryApi from '../services/galleryApi';

// Smart image URL: converts /images/ to /api/images/ for MongoDB-backed serving
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/images/')) return '/api' + url;
  return url;
};

const CATEGORIES = [
  { id: 'jewelry', name: 'Jewelry & Watches' },
  { id: 'collectibles', name: 'Collectibles & Figurines' },
  { id: 'ceramics', name: 'Ceramics & Pottery' },
  { id: 'sculpture', name: 'Sculpture & Bronze' },
  { id: 'art', name: 'Art & Paintings' }
];

const CONDITIONS = ['Excellent', 'Good', 'Fair', 'As Found'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'jewelry',
    description: '',
    price: '',
    image: '',
    images: [],
    year: '',
    condition: 'Good'
  });

  const admin = adminApi.getAdmin();

  const loadItems = useCallback(async () => {
    try {
      const data = await galleryApi.getItems({ limit: 500 });
      setItems(data);
    } catch (err) {
      setError('Failed to load items');
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await galleryApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (!adminApi.isAuthenticated()) {
        navigate('/admin');
        return;
      }
      
      const valid = await adminApi.verifyToken();
      if (!valid) {
        navigate('/admin');
        return;
      }
      
      await Promise.all([loadItems(), loadStats()]);
      setLoading(false);
    };
    
    checkAuth();
  }, [navigate, loadItems, loadStats]);

  const handleLogout = () => {
    adminApi.logout();
    navigate('/admin');
  };

  // Compress image before upload to handle large phone photos
  const compressImage = (file, maxWidth = 1600, quality = 0.8) => {
    return new Promise((resolve) => {
      // Skip compression for small files (under 1MB)
      if (file.size < 1024 * 1024) {
        resolve(file);
        return;
      }
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          const compressed = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          resolve(compressed);
        }, 'image/jpeg', quality);
      };
      
      img.onerror = () => resolve(file); // Fallback to original if compression fails
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    // Check file sizes before uploading
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        setError(`File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 10MB.`);
        e.target.value = '';
        return;
      }
    }
    
    setUploadingImage(true);
    setError('');
    
    try {
      if (files.length === 1) {
        // Compress then upload single file
        const compressed = await compressImage(files[0]);
        const result = await adminApi.uploadImage(compressed);
        const newImages = [...(formData.images || []), result.url];
        setFormData({ 
          ...formData, 
          image: formData.image || result.url,
          images: newImages 
        });
        setSuccess('Image uploaded successfully');
      } else {
        // Compress then upload multiple files
        const compressedFiles = await Promise.all(
          Array.from(files).map(f => compressImage(f))
        );
        const result = await adminApi.uploadMultipleImages(compressedFiles);
        const newImages = [...(formData.images || []), ...result.uploaded];
        setFormData({ 
          ...formData, 
          image: formData.image || result.uploaded[0],
          images: newImages 
        });
        setSuccess(`${result.uploaded.length} images uploaded`);
        if (result.failed.length > 0) {
          setError(`${result.failed.length} failed to upload`);
        }
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const msg = err.message || 'Upload failed';
      if (msg.includes('413') || msg.includes('too large') || msg.includes('payload')) {
        setError('Image too large. Try a smaller photo or reduce the image size.');
      } else if (msg.includes('401') || msg.includes('token') || msg.includes('auth')) {
        setError('Session expired. Please log out and log in again.');
      } else {
        setError(`Upload failed: ${msg}`);
      }
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const removeImage = (indexToRemove) => {
    const newImages = formData.images.filter((_, index) => index !== indexToRemove);
    setFormData({
      ...formData,
      images: newImages,
      image: newImages[0] || '' // Update primary image
    });
  };

  const setPrimaryImage = (index) => {
    setFormData({
      ...formData,
      image: formData.images[index]
    });
    setSuccess('Primary image updated');
    setTimeout(() => setSuccess(''), 2000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'jewelry',
      description: '',
      price: '',
      image: '',
      images: [],
      year: '',
      condition: 'Good'
    });
    setEditingItem(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (item) => {
    const existingImages = (item.images && item.images.length > 0) 
      ? item.images 
      : (item.image ? [item.image] : []);
    setFormData({
      name: item.name,
      category: item.category,
      description: item.description,
      price: item.price,
      image: item.image,
      images: existingImages,
      year: item.year,
      condition: item.condition
    });
    setEditingItem(item);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      if (editingItem) {
        await adminApi.updateItem(editingItem.id, formData);
        setSuccess('Item updated successfully');
      } else {
        await adminApi.createItem(formData);
        setSuccess('Item created successfully');
      }
      
      await Promise.all([loadItems(), loadStats()]);
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteItem(id);
      await Promise.all([loadItems(), loadStats()]);
      setDeleteConfirm(null);
      setSuccess('Item deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
              <LayoutGrid className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-sm text-gray-400">Welcome, {admin?.username}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              View Site
            </Button>
            <Button 
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4 text-center">
                <Package className="w-8 h-8 text-teal-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{stats.total_items}</p>
                <p className="text-sm text-gray-400">Total Items</p>
              </CardContent>
            </Card>
            {CATEGORIES.slice(0, 4).map(cat => (
              <Card key={cat.id} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4 text-center">
                  <p className="text-xl font-bold text-white">{stats.by_category[cat.id] || 0}</p>
                  <p className="text-xs text-gray-400">{cat.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-800 border-gray-700 text-white"
              data-testid="admin-search-input"
            />
          </div>
          <Button 
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-teal-600 hover:bg-teal-500"
            data-testid="admin-add-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl bg-gray-800 border-gray-700 max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">
                  {editingItem ? 'Edit Item' : 'Add New Item'}
                </CardTitle>
                <button onClick={resetForm} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-300">Name *</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-gray-700 border-gray-600 text-white"
                        required
                        data-testid="item-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm text-gray-300">Category *</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                        required
                        data-testid="item-category-select"
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Description *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 text-white min-h-[100px]"
                      required
                      data-testid="item-description-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-300">Price *</label>
                      <Input
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="£50"
                        className="bg-gray-700 border-gray-600 text-white"
                        required
                        data-testid="item-price-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm text-gray-300">Year/Period *</label>
                      <Input
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        placeholder="Victorian"
                        className="bg-gray-700 border-gray-600 text-white"
                        required
                        data-testid="item-year-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm text-gray-300">Condition *</label>
                      <select
                        value={formData.condition}
                        onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                        className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                        required
                        data-testid="item-condition-select"
                      >
                        {CONDITIONS.map(cond => (
                          <option key={cond} value={cond}>{cond}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <label className="text-sm text-gray-300">Images * <span className="text-gray-500">(You can add multiple)</span></label>
                    
                    {/* Uploaded images grid */}
                    {formData.images && formData.images.length > 0 && (
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        {formData.images.map((img, index) => (
                          <div key={index} className="relative group">
                            <img 
                              src={getImageUrl(img)} 
                              alt={`Image ${index + 1}`} 
                              className={`w-full h-24 object-cover rounded-lg ${formData.image === img ? 'ring-2 ring-teal-500' : ''}`}
                              onError={(e) => { e.target.onerror = null; e.target.style.opacity = '0.3'; }}
                            />
                            {formData.image === img && (
                              <span className="absolute top-1 left-1 bg-teal-500 text-white text-xs px-1.5 py-0.5 rounded">Main</span>
                            )}
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                              {formData.image !== img && (
                                <button
                                  type="button"
                                  onClick={() => setPrimaryImage(index)}
                                  className="p-1.5 bg-teal-500 rounded-full hover:bg-teal-400"
                                  title="Set as main image"
                                >
                                  <Check className="w-4 h-4 text-white" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="p-1.5 bg-red-500 rounded-full hover:bg-red-400"
                                title="Remove image"
                              >
                                <X className="w-4 h-4 text-white" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload buttons */}
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-all ${
                      formData.images?.length > 0 ? 'border-gray-700' : 'border-gray-600'
                    }`}>
                      {uploadingImage ? (
                        <div className="py-4">
                          <Loader2 className="w-10 h-10 animate-spin text-teal-500 mx-auto" />
                          <p className="text-gray-400 text-sm mt-2">Uploading...</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-center gap-4">
                            {/* Take Photo Button */}
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleImageUpload}
                                className="hidden"
                              />
                              <div className="flex flex-col items-center p-3 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors">
                                <ImageIcon className="w-8 h-8 text-teal-400 mb-1" />
                                <span className="text-white text-sm font-medium">Take Photo</span>
                              </div>
                            </label>
                            
                            {/* Choose from Gallery Button - allows multiple */}
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                className="hidden"
                              />
                              <div className="flex flex-col items-center p-3 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors">
                                <Upload className="w-8 h-8 text-teal-400 mb-1" />
                                <span className="text-white text-sm font-medium">Gallery</span>
                              </div>
                            </label>
                          </div>
                          <p className="text-gray-500 text-xs mt-3">
                            {formData.images?.length > 0 ? 'Add more photos' : 'JPG, PNG, WebP up to 10MB each'}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-4 pt-4">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-teal-600 hover:bg-teal-500"
                      disabled={submitting}
                      data-testid="item-submit-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        editingItem ? 'Update Item' : 'Create Item'
                      )}
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={resetForm}
                      className="border-gray-600 text-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Delete Item?</h3>
                <p className="text-gray-400 mb-6">
                  Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
                </p>
                <div className="flex gap-4">
                  <Button 
                    onClick={() => handleDelete(deleteConfirm.id)}
                    className="flex-1 bg-red-600 hover:bg-red-500"
                    data-testid="confirm-delete-btn"
                  >
                    Delete
                  </Button>
                  <Button 
                    onClick={() => setDeleteConfirm(null)}
                    variant="outline"
                    className="flex-1 border-gray-600 text-gray-300"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map(item => (
            <Card key={item.id} className="bg-gray-800 border-gray-700 overflow-hidden group">
              <div className="aspect-square relative overflow-hidden bg-gray-700">
                {(item.images?.length > 0 ? item.images[0] : item.image) ? (
                  <img 
                    src={getImageUrl(item.images?.length > 0 ? item.images[0] : item.image)} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-gray-600" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button 
                    size="sm"
                    onClick={() => handleEdit(item)}
                    className="bg-teal-600 hover:bg-teal-500"
                    data-testid={`edit-item-${item.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setDeleteConfirm(item)}
                    className="bg-red-600 hover:bg-red-500"
                    data-testid={`delete-item-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <Badge className="absolute top-2 right-2 bg-teal-600">
                  {item.price}
                </Badge>
              </div>
              <CardContent className="p-4">
                <h3 className="font-medium text-white truncate mb-1">{item.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{item.category}</span>
                  <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                    {item.condition}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No items found</h3>
            <p className="text-gray-400">
              {searchQuery ? 'Try a different search term' : 'Add your first item to get started'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
